import { useEffect, useRef } from 'react';
import type { UiLayout } from '@forq/platform-data';

/** Term-Key-Präfix für freie Text-Umbenennungen (Original-String → Ersatz), im „terms"-Layout. */
export const TEXT_TERM_PREFIX = 'text:';

/** Baut die Wörterbuch-Map (Original → Ersatz) aus den `text:`-Einträgen eines terms-Layouts. */
export function textOverrideMap(terms: UiLayout): Map<string, string> {
  const map = new Map<string, string>();
  for (const [key, value] of Object.entries(terms.texts)) {
    if (key.startsWith(TEXT_TERM_PREFIX)) {
      const original = key.slice(TEXT_TERM_PREFIX.length);
      if (original && value) map.set(original, value);
    }
  }
  return map;
}

const SKIP_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'SCRIPT', 'STYLE', 'SVG', 'PATH']);

function eligible(node: Text): boolean {
  const parent = node.parentElement;
  if (!parent) return false;
  if (SKIP_TAGS.has(parent.tagName)) return false;
  if (parent.isContentEditable) return false;
  return true;
}

/**
 * Wendet ein Text-Wörterbuch (Original→Ersatz) auf alle Textknoten unter `rootSelector` an. Bewusst
 * defensiv, damit es in einer laufenden React-App nichts kaputt macht:
 * - ersetzt NUR exakte (getrimmte) Treffer der vom Admin gewählten Originale — dynamische Werte
 *   (Zahlen, Daten, Namen) stehen nie im Wörterbuch und bleiben unberührt;
 * - merkt je ersetztem Knoten das Original (persistente WeakMap) → wird ein Eintrag entfernt, stellt
 *   der nächste Lauf das Original wieder her;
 * - läuft bei neu gerenderten Knoten erneut (MutationObserver nur auf childList → das eigene Setzen von
 *   textContent ist eine characterData-Änderung und löst KEINE Schleife aus);
 * - tut nichts, solange das Wörterbuch leer ist (kein Overhead im Normalbetrieb).
 */
export function useApplyTextOverrides(overrides: ReadonlyMap<string, string>, rootSelector = '.forq-content'): void {
  const originalsRef = useRef<WeakMap<Text, string>>(new WeakMap());

  useEffect(() => {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    const originals = originalsRef.current;

    const applyToNode = (node: Text) => {
      if (!eligible(node)) return;
      const known = originals.get(node);
      const original = known ?? (node.textContent ?? '');
      const trimmed = original.trim();
      if (!trimmed) return;
      const replacement = overrides.get(trimmed);
      if (replacement != null) {
        if (node.textContent !== replacement) node.textContent = replacement;
        if (known == null) originals.set(node, original);
      } else if (known != null) {
        if (node.textContent !== known) node.textContent = known;
        originals.delete(node);
      }
    };

    const walk = (target: Node) => {
      const it = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
      for (let n = it.nextNode(); n; n = it.nextNode()) applyToNode(n as Text);
    };

    walk(root);
    // Kein Wörterbuch -> kein Beobachter (der Walk oben hat evtl. entfernte Einträge schon zurückgesetzt).
    if (overrides.size === 0) return;

    let raf = 0;
    const pending: Node[] = [];
    const flush = () => {
      raf = 0;
      const batch = pending.splice(0);
      for (const node of batch) {
        if (!node.isConnected) continue;
        if (node.nodeType === Node.TEXT_NODE) applyToNode(node as Text);
        else if (node.nodeType === Node.ELEMENT_NODE) walk(node);
      }
    };
    const observer = new MutationObserver((records) => {
      for (const record of records) record.addedNodes.forEach((node) => pending.push(node));
      if (pending.length && !raf) raf = window.requestAnimationFrame(flush);
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => { observer.disconnect(); if (raf) window.cancelAnimationFrame(raf); };
  }, [overrides, rootSelector]);
}
